/*
 * OLD STYLE PROCESSOR LEGACY MIXINS
 * ---------------------------------
 * This file includes Style Processor Formatters that could be used in st.css file.
 *
 * Important Note - Since Stylable js mixins is not support es6 syntax, this file should be written in CommonJS.
 * Meaning you shouldn't use import - export and you must use the module.exports syntax.
 */

const applyStyleMethod = (func, ...args) => {
  const unquotedArgs = args.map((arg) => arg.replace(/"/g, '')).join(', ');
  return `"${func}(${unquotedArgs})"`;
};

const createStyleFunc =
  (func) =>
  (...args) =>
    applyStyleMethod(func, ...args);

module.exports.applyStyleMethod = applyStyleMethod;

module.exports.fallback = createStyleFunc('fallback');
module.exports.zeroAsTrue = createStyleFunc('zeroAsTrue');
module.exports.color = createStyleFunc('color');
module.exports.applyUnit = createStyleFunc('unit');
module.exports.applyOpacity = createStyleFunc('opacity');
module.exports.calculate = createStyleFunc('calculate');
module.exports.string = createStyleFunc('string');
module.exports.font = createStyleFunc('font');
module.exports.join = createStyleFunc('join');
module.exports.withoutOpacity = createStyleFunc('withoutOpacity');
module.exports.darken = createStyleFunc('darken');
module.exports.smartContrast = createStyleFunc('smartContrast');
module.exports.number = createStyleFunc('number');
module.exports.unit = createStyleFunc('unit');

/*
 * ---------------------------------------------------------------------------------
 * NEW CSS VARIABLES SOLUTION MIXINS
 * wix-ui-tpa internal mixins
 * ---------------------------------------------------------------------------------
 */

/**
 * A helper function for multiple vars overrides
 * var(--var1-param, var(--var2-param, ...))
 */
const createParamVar = (names, param, fallbacks = []) => {
  if (names.startsWith('(')) {
    names = names.substring(1, names.length - 1).split(',');
  } else {
    names = [names];
  }

  let str = '';
  names.forEach((name, i) => {
    if (param) {
      str += `var(${name.trim()}-${param}`;
    } else {
      str += `var(${name.trim()}`;
    }

    if (i < names.length - 1) {
      str += ', ';
    } else {
      fallbacks
        .filter((x) => x)
        .forEach((fallback) => {
          str += `, ${fallback}`;
        });
    }
  });
  str += ')'.repeat(names.length);

  return str;
};

const fontProperties = {
  'text-decoration': 'text-decoration',
  'line-height': 'line-height',
  family: 'font-family',
  size: 'font-size',
  style: 'font-style',
  variant: 'font-variant',
  weight: 'font-weight',
};
module.exports.fontProperties = fontProperties;

/**
 * Create a full static font definition (without a user variable override) util function
 * @param preset The font preset
 * @param params A string representing an object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight'
 */
const createStaticFont = (preset, params) => {
  const constituentProperties = {};
  Object.entries(fontProperties).map(([key, value]) => {
    constituentProperties[value] = params[key]
      ? params[key]
      : `var(--wix-font-${preset}-${key})`;
  });
  return constituentProperties;
};

/**
 * Create a full font definition (with a user variable override) util function
 * @param name The overridable variable name (must be global)
 * @param preset The font preset
 * @param params An object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight'
 */
const createOverridableFont = (name, preset, params) => {
  const constituentProperties = {};
  Object.entries(fontProperties).map(([key, value]) => {
    const fallback = params[key]
      ? params[key]
      : `var(--wix-font-${preset}-${key})`;
    constituentProperties[value] = createParamVar(name, key, [fallback]);
  });
  return constituentProperties;
};

/**
 * Create a full font definition (with a user variable override) util function without fallback preset
 * @param name The overridable variable name (must be global)
 * @param params An object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight'
 */
const createOverridableFonWithoutFallbackPreset = (name, params) => {
  const constituentProperties = {};
  Object.entries(fontProperties).map(([key, value]) => {
    const fallback = params[key];
    constituentProperties[value] = createParamVar(
      name,
      key,
      fallback ? [fallback] : [],
    );
  });
  return constituentProperties;
};

/**
 * Create a full font definition
 * @param name The overridable variable name (must be global)
 * @param preset The font preset
 * @param overrides A string representing an object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight'
 *
 * Usage example:
 *   -st-mixin: createFont(--main-font, 'Body-M', '{ "size": "16px" }');
 *   -st-mixin: createFont(null, 'Body-M', '{ "size": "16px" }');
 *   -st-mixin: createFont(--main-font, null, '{ "size": "16px" }');
 */
module.exports.createFont = ([name, preset, overrides = '{}']) => {
  const params = JSON.parse(overrides);

  if (name === 'null') {
    return createStaticFont(preset, params);
  }

  if (preset === 'null') {
    return createOverridableFonWithoutFallbackPreset(name, params);
  }

  return createOverridableFont(name, preset, params);
};

/**
 * Removes redundant "var" util function
 * @param str {string}
 * @returns {string}
 */
const resolveVar = (str) =>
  str.startsWith('var(') ? str : str.startsWith('--') ? `var(${str})` : str;

// Helper function for createColor
const createColorRaw = (variable, rgb, opacity) => {
  if (opacity) {
    return `${createParamVar(variable, 'rgb', [
      resolveVar(rgb),
    ])}, calc(${createParamVar(variable, 'opacity', ['1'])} * ${opacity})`;
  }

  if (rgb.startsWith('(')) {
    const params = rgb.substring(1, rgb.length - 1).split(',');
    return `${createParamVar(variable, undefined, [
      `${resolveVar(params[0].trim())}, ${params[1].trim()}`,
    ])}`;
  }

  return createParamVar(variable, undefined, [resolveVar(rgb)]);
};
module.exports.createColorRaw = createColorRaw;

/**
 * Create color RGB and opacity
 * @param variable The variable name required by wix-ui-tpa (must be global)
 * @param rgb A string representing "r, g, b" (can be a css variable)
 * @param opacity [optional] Opacity of the color (default is 1)
 *
 * Usage example:
 *   color: createColor(--main-color, var(--wix-color-1)); // Without opacity
 *   color: createColor(--main-color, var(--wix-color-1), 0.5); // The main/fallback color will get opacity
 *   color: createColor(--main-color, (var(--wix-color-1), 0.5)); // Only the fallback color will get opacity
 */

module.exports.createColor = (variable, rgb, opacity) => {
  return `rgb(${createColorRaw(variable, rgb, opacity)})`;
};

/*
 * ---------------------------------------------------------------------------------
 * NEW CSS VARIABLES SOLUTION MIXINS
 * wix-ui-tpa consumer mixins
 * ---------------------------------------------------------------------------------
 */

/**
 * Creates a static font definition (without a user variable override) util function
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param preset The font preset
 * @param params [optional] A string representing an object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight'
 */
const setStaticFont = (var1, preset, params) => {
  const constituentProperties = {};
  Object.keys(fontProperties).map((key) => {
    constituentProperties[`${var1}-${key}`] = params[key]
      ? params[key]
      : `var(--wix-font-${preset}-${key})`;
  });
  return constituentProperties;
};

/**
 * Creates a static font definition (without a user variable override) util function
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param var2 The variable name with the user params (must be global)
 * @param preset The font preset
 * @param params [optional] A string representing an object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight'
 */
const setOverridableFont = (var1, var2, preset, params) => {
  const constituentProperties = {};
  Object.keys(fontProperties).map((key) => {
    if (preset) {
      const fallback = params[key]
        ? params[key]
        : `var(--wix-font-${preset}-${key})`;

      constituentProperties[`${var1}-${key}`] = createParamVar(var2, key, [
        fallback,
      ]);
    } else {
      constituentProperties[`${var1}-${key}`] = createParamVar(var2, key);
    }
  });
  return constituentProperties;
};

/**
 * Font to constituent properties
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param var2 The variable name with the user params (must be global)
 * @param preset The font preset
 * @param overrides [optional] A string representing an object with the following keys: 'family', 'line-height', 'size', 'style', 'variant', 'weight', 'text-decoration'
 *
 * Usage example:
 *   -st-mixin: setFont(--main-font, --my-font, 'Body-M', '{ "size": "16px" }');
 *   -st-mixin: setFont(--main-font, null, 'Body-M', '{ "size": "16px" }');
 */
module.exports.setFont = ([var1, var2, preset, overrides = '{}']) => {
  const params = JSON.parse(overrides);

  if (var2 === 'null') {
    return setStaticFont(var1, preset, params);
  }

  return setOverridableFont(var1, var2, preset, params);
};

/**
 * Color to RGB and opacity
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param rgb [optional] The fallback color - string "R, G, B"
 * @param opacity [optional] The fallback opacity
 */
const setStaticColor = (var1, rgb, opacity = 1) => ({
  [var1]: `${rgb}, ${opacity}`,
  [`${var1}-rgb`]: `${rgb}`,
  [`${var1}-opacity`]: `${opacity}`,
});

/**
 * Color to RGB and opacity
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param var2 The variable name with the user params (must be global)
 * @param rgb [optional] The fallback color - string "R, G, B"
 * @param opacity [optional] The fallback opacity
 */
const setOverridableColor = (var1, var2, rgb, opacity) => ({
  [var1]: createParamVar(var2, undefined, [rgb, opacity]),
  [`${var1}-rgb`]: createParamVar(var2, 'rgb', [rgb]),
  [`${var1}-opacity`]: createParamVar(var2, 'opacity', [opacity]),
});

/**
 * Color to RGB and opacity
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param var2 The variable name with the user params (must be global)
 * @param rgb [optional] The fallback color - string "R, G, B"
 * @param opacity [optional] The fallback opacity
 *
 * Usage example:
 *   -st-mixin: setColor(--main-color, --my-color, --wix-color-1, 0.5);
 *   -st-mixin: setColor(--main-color, null, --wix-color-1, 0.5);
 */
module.exports.setColor = ([var1, var2, rgb, opacity]) => {
  if (var2 === 'null') {
    return setStaticColor(var1, rgb, opacity);
  }

  return setOverridableColor(var1, var2, rgb, opacity);
};

/**
 * Creates a static general variable definition (without a user variable override) util function
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param units [optional] Units, will be unitless if not provided or null - e.g. px, em, %
 * @param fallback [optional] The fallback size - e.g. 1px, 1em, 1%
 */
const setStaticVar = (var1, units, fallback) => {
  let value = `${fallback}`;
  if (units && units !== 'null') {
    value = `calc(${value} * 1${units})`;
  }
  return {
    [var1]: value,
  };
};

/**
 * Creates a static general variable definition (with a user variable override) util function
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param var2 The variable name with the user params (must be global)
 * @param units [optional] Units, will be unitless if not provided or null - e.g. px, em, %
 * @param fallback [optional] The fallback size - e.g. 1px, 1em, 1%
 */
const setOverridableVar = (var1, var2, units, fallback) => {
  let value = createParamVar(var2, undefined, [fallback]);
  if (units && units !== 'null') {
    value = `calc(${value} * 1${units})`;
  }

  return {
    [var1]: value,
  };
};

/**
 * Size
 * @param var1 The variable name required by wix-ui-tpa (must be global)
 * @param var2 The variable name with the user params (must be global)
 * @param units [optional] Units, will be unitless if not provided - e.g. px, em, %
 * @param fallback [optional] A unitless fallback size - e.g. -1, 0.5, 80
 *
 * Usage example:
 *   -st-mixin: setVar(--main-size, --my-size, px, 1);
 *   -st-mixin: setVar(--main-size, null, px, 1);
 */
module.exports.setVar = ([var1, var2, units, fallback]) => {
  if (var2 === 'null') {
    return setStaticVar(var1, units, fallback);
  }

  return setOverridableVar(var1, var2, units, fallback);
};

module.exports.createButtonCorners = ([type]) => {
  return type === 'primary' || type === 'secondary'
    ? {
        'border-start-start-radius': `var(--wix-ui-tpa-button-main-border-start-start-radius, var(--wix-ui-tpa-button-main-border-radius, var(--wst-button-${type}-border-top-left-radius, 0)))`,
        'border-start-end-radius': `var(--wix-ui-tpa-button-main-border-start-end-radius, var(--wix-ui-tpa-button-main-border-radius, var(--wst-button-${type}-border-top-right-radius, 0)))`,
        'border-end-start-radius': `var(--wix-ui-tpa-button-main-border-end-start-radius, var(--wix-ui-tpa-button-main-border-radius, var(--wst-button-${type}-border-bottom-left-radius, 0)))`,
        'border-end-end-radius': `var(--wix-ui-tpa-button-main-border-end-end-radius, var(--wix-ui-tpa-button-main-border-radius, var(--wst-button-${type}-border-bottom-right-radius, 0)))`,
      }
    : {
        'border-start-start-radius': `var(--wix-ui-tpa-button-main-border-start-start-radius, var(--wix-ui-tpa-button-main-border-radius, 0))`,
        'border-start-end-radius': `var(--wix-ui-tpa-button-main-border-start-end-radius, var(--wix-ui-tpa-button-main-border-radius, 0))`,
        'border-end-start-radius': `var(--wix-ui-tpa-button-main-border-end-start-radius, var(--wix-ui-tpa-button-main-border-radius, 0))`,
        'border-end-end-radius': `var(--wix-ui-tpa-button-main-border-end-end-radius, var(--wix-ui-tpa-button-main-border-radius, 0))`,
      };
};

module.exports.createButtonFont = ([type, state, preset]) => {
  const fallbacks = {
    size: 'var(--wix-ui-tpa-button-font-size-default)',
    'line-height': 'var(--wix-ui-tpa-button-line-height-default)',
  };
  const constituentProperties = {};
  Object.entries(fontProperties).map(([key, value]) => {
    const themeFallback =
      type === 'basic' || type === 'basicSecondary'
        ? `var(--wst-paragraph-2-${value}, var(--wix-font-${preset}-${key}))`
        : `var(--wst-button-${type}-${value}, var(--wst-paragraph-2-${value}, var(--wix-font-${preset}-${key})))`;
    const fallback = fallbacks[key] ?? themeFallback;

    constituentProperties[value] =
      state === 'main'
        ? `var(--wix-ui-tpa-button-main-text-font-${key}, ${fallback})`
        : `var(--wix-ui-tpa-button-hover-text-font-${key}, var(--wix-ui-tpa-button-main-text-font-${key}, ${fallback}))`;
  });
  return constituentProperties;
};

const isBasicButton = (type) => !(type === 'primary' || type === 'secondary');
const wutMainBorder = `--wix-ui-tpa-button-main-border`;
const wutMainBorderColor = `${wutMainBorder}-color`;
const wutHoverBorderColor = `--wix-ui-tpa-button-hover-border-color`;
const wutDisabledBorderColor = `--wix-ui-tpa-button-disabled-border-color`;
const wutMainBorderLeft = `--wix-ui-tpa-button-main-border-inline-start`;
const wutMainBorderRight = `--wix-ui-tpa-button-main-border-inline-end`;
const wutMainBorderTop = `--wix-ui-tpa-button-main-border-block-start`;
const wutMainBorderBottom = `--wix-ui-tpa-button-main-border-block-end`;
const wutMainBorderLeftColor = `${wutMainBorderLeft}-color`;
const wutMainBorderRightColor = `${wutMainBorderRight}-color`;
const wutMainBorderTopColor = `${wutMainBorderTop}-color`;
const wutMainBorderBottomColor = `${wutMainBorderBottom}-color`;

const createBorderFallback = ([type, side, property, fallback]) => {
  return isBasicButton(type)
    ? `var(${fallback})`
    : `var(--wst-button-${type}-border-${side}-${property}, var(${fallback}))`;
};

const createMainBorderColorLonghand = ([fallbacks]) => {
  return {
    [`border-inline-start-color`]: `rgb(var(${wutMainBorderLeftColor}, var(${wutMainBorderColor}, ${fallbacks.left})))`,
    [`border-block-start-color`]: `rgb(var(${wutMainBorderTopColor}, var(${wutMainBorderColor}, ${fallbacks.top})))`,
    [`border-inline-end-color`]: `rgb(var(${wutMainBorderRightColor}, var(${wutMainBorderColor}, ${fallbacks.right})))`,
    [`border-block-end-color`]: `rgb(var(${wutMainBorderBottomColor}, var(${wutMainBorderColor}, ${fallbacks.bottom})))`,
  };
};

const createHoverBorderColorLonghand = ([fallbacks]) => {
  return {
    [`border-inline-start-color`]: `rgb(var(${wutHoverBorderColor}, var(${wutMainBorderLeftColor}, var(${wutMainBorderColor}, ${fallbacks.left})))`,
    [`border-block-start-color`]: `rgb(var(${wutHoverBorderColor}, var(${wutMainBorderTopColor}, var(${wutMainBorderColor}, ${fallbacks.top})))`,
    [`border-inline-end-color`]: `rgb(var(${wutHoverBorderColor}, var(${wutMainBorderRightColor}, var(${wutMainBorderColor}, ${fallbacks.right})))`,
    [`border-block-end-color`]: `rgb(var(${wutHoverBorderColor}, var(${wutMainBorderBottomColor}, var(${wutMainBorderColor}, ${fallbacks.bottom})))`,
  };
};

const createDisabledBorderColorLonghand = ([fallbacks]) => {
  return {
    [`border-inline-start-color`]: `rgb(var(${wutDisabledBorderColor},  ${fallbacks.left}))`,
    [`border-block-start-color`]: `rgb(var(${wutDisabledBorderColor}, ${fallbacks.top}))`,
    [`border-inline-end-color`]: `rgb(var(${wutDisabledBorderColor}, ${fallbacks.right}))`,
    [`border-block-end-color`]: `rgb(var(${wutDisabledBorderColor}, ${fallbacks.bottom}))`,
  };
};

const createBorderColorLonghand = ([type, state, fallback]) => {
  const left = createBorderFallback([type, 'left', 'color-rgb', fallback]);
  const top = createBorderFallback([type, 'top', 'color-rgb', fallback]);
  const right = createBorderFallback([type, 'right', 'color-rgb', fallback]);
  const bottom = createBorderFallback([type, 'bottom', 'color-rgb', fallback]);

  const fallbacks = {
    left,
    top,
    right,
    bottom,
  };

  switch (state) {
    case 'hover':
      return createHoverBorderColorLonghand([fallbacks]);
    case 'disabled':
      return createDisabledBorderColorLonghand([fallbacks]);
    case 'main':
      return createMainBorderColorLonghand([fallbacks]);
  }
};

const createBorderLonghand = ([type, property, fallback]) => {
  const left = createBorderFallback([type, 'left', property, fallback]);
  const top = createBorderFallback([type, 'top', property, fallback]);
  const right = createBorderFallback([type, 'right', property, fallback]);
  const bottom = createBorderFallback([type, 'bottom', property, fallback]);

  return {
    [`border-inline-start-${property}`]: `var(${wutMainBorderLeft}-${property}, var(${wutMainBorder}-${property}, ${left}))`,
    [`border-block-start-${property}`]: `var(${wutMainBorderTop}-${property}, var(${wutMainBorder}-${property}, ${top}))`,
    [`border-inline-end-${property}`]: `var(${wutMainBorderRight}-${property}, var(${wutMainBorder}-${property}, ${right}))`,
    [`border-block-end-${property}`]: `var(${wutMainBorderBottom}-${property}, var(${wutMainBorder}-${property}, ${bottom}))`,
  };
};

const createBorderWidthLonghand = ([type, fallback]) =>
  createBorderLonghand([type, 'width', fallback]);
const createBorderStyleLonghand = ([type, fallback]) =>
  createBorderLonghand([type, 'style', fallback]);

/**
 * Create primary and secondary button borders
 * @param type The button type (primary or secondary)
 * @param state The button state (main, hover)
 * @param fallbackWidth The fallback border width
 * @param fallbackStyle The fallback border style
 * @param fallbackColor The fallback border color
 */
module.exports.createButtonBorders = ([
  type,
  state,
  fallbackWidth,
  fallbackStyle,
  fallbackColor,
]) => {
  const widthProps = createBorderWidthLonghand([type, fallbackWidth]);
  const styleProps = createBorderStyleLonghand([type, fallbackStyle]);
  const colorProps = createBorderColorLonghand([type, state, fallbackColor]);
  return { ...widthProps, ...styleProps, ...colorProps };
};

/**
 * Create a new font from existing font and overrides.
 * @param newFont The font to be created.
 * @param existingFont The font which values will be used in new font.
 * @param overrides Overrides will take priority before existingFont.
 * @returns newFont constituent properties
 *
 * Usage example:
 *   -st-mixin: overrideFont(--wst-paragraph-2-overriden-font, --wst-paragraph-2, '{ "line-height" : "1.5" }');
 */
module.exports.createFontWithOverrides = ([
  newFont,
  existingFont,
  overrides = {},
]) => {
  const parsedOverrides = JSON.parse(overrides);
  const constituentProperties = {};
  Object.entries(fontProperties).map(([key, value]) => {
    constituentProperties[`${newFont}-${key}`] = parsedOverrides[key]
      ? parsedOverrides[key]
      : `var(${existingFont}-${value})`;
  });
  return constituentProperties;
};
